import { ComponentStyleConfig, IconButton } from "@chakra-ui/react";
import { SystemStyleObject } from "@chakra-ui/styled-system";
import { theme as chakraTheme } from "@chakra-ui/theme";
import { mode } from "@chakra-ui/theme-tools";
import { transparentize } from "color2k";
import colors from "theme/colors";
import { shadows } from "theme/shadows";

const defaultButtonTheme = chakraTheme.components.Button;

const ButtonTheme: ComponentStyleConfig = {
  baseStyle: {
    minWidth: "min-content",
    transitionProperty: "common",
    transitionDuration: "normal",
    textStyle: "bodyBold",
    textDecoration: "none",
    _hover: {
      textDecoration: "none",
    },
    _disabled: {
      cursor: "not-allowed",
      _active: {
        shadow: "none",
        bg: "none",
      },
      _hover: {
        bg: "none",
      },
      "& .chakra-icon": {
        color: "gray.300",
      },
    },
    _focusVisible: {
      shadow: "outline",
    },
    overflow: "hidden",
  },
  variants: {
    ghostDarken: (props) => ({
      bg: "transparent",
      svg: {
        color: "gray.400",
      },

      color: "gray.400",

      _hover: {
        bg: "transparent",
        color: mode("gray.800", "gray.400")(props), // Darkens the icon on hover
        svg: {
          color: mode("gray.800", "gray.400")(props), // Darkens the icon on hover
        },
      },
      _active: {
        bg: "transparent",
      },
      _focus: {
        boxShadow: "none",
      },
    }),
    solid: ({ colorScheme }) => ({
      ...solidStylesForColorScheme[colorScheme],
      borderWidth: "1px",
      borderRadius: "6px",
      _focusVisible: {
        shadow: "xsFocusedPrimary100",
      },
    }),
    subtle: ({ colorScheme }) => ({
      ...defaultButtonTheme.variants!.ghost,
      ...subtleStylesForColorScheme[colorScheme],
      p: "2px",
      height: "unset",
      minHeight: "unset",
      borderRadius: "4px",
    }),
    text: ({ colorScheme }) => ({
      ...subtleStylesForColorScheme[colorScheme],
      textDecoration: "underline",
      textStyle: "normal",
      _hover: {
        textDecoration: "underline",
      },
      _disabled: {
        cursor: "not-allowed",
        _hover: {},
        "& .chakra-icon": {
          color: "gray.300",
        },
      },
      overflow: "hidden",
    }),
    ghost: ({ colorScheme }) => ({
      ...subtleStylesForColorScheme[colorScheme],
      borderRadius: "6px",
      _hover: {
        bg: "gray.50",
      },
      _active: {
        bg: "gray.100",
        shadow: shadows.press,
      },
    }),
    select: {
      borderRadius: 4,
      borderWidth: 1,
      backgroundColor: "white",
      borderColor: "gray.300",
      color: "gray.700",
      width: "100%",
      height: "48px",
      pr: "12px",
      _focusVisible: {
        borderColor: "primary.400",
        "&&": {
          outline: "none",
          boxShadow: "none",
        },
      },
      _active: {
        borderColor: "primary.400",
      },
      _invalid: {
        backgroundColor: "red.50",
        borderColor: "red.600",
        color: "red.600",
      },
      _disabled: {
        cursor: "not-allowed",
        "&&": {
          borderColor: "gray.300",
        },
      },
    },
  },
  sizes: {
    xs: {
      py: "5px", // 6px - 1px border
      px: "12px",
      minHeight: "32px",
      height: "unset",
    },
    sm: {
      py: "7px", // 8px - 1px border
      px: "14px",
      fontSize: 14,
      minHeight: "32px",
      height: "unset",
    },
    md: {
      py: "9px", // 10px - 1px border
      px: "16px",
      fontSize: 14,
      minHeight: "40px",
      height: "unset",
    },
    lg: {
      py: "11px", // 10px - 1px border
      px: "18px",
      fontSize: 16,
      minHeight: "40px",
      height: "unset",
    },
    "xxs-icon": {
      "&&": {
        p: 0,
        borderRadius: "2px",
        w: "16px",
        h: "16px",
        "& svg": {
          w: "16px",
          h: "16px",
        },
      },
    },
    "xs-icon": {
      "&&": {
        py: "5px", // 6px - 1px border
        px: "11px", // 12px - 1px border
      },
    },
    "sm-icon": {
      "&&": {
        width: "24px",
        height: "24px",
        "&.chakra-menu__menu-button": {
          paddingInline: 0,
        },
        svg: {
          w: "20px",
          h: "20px",
        },
      },
    },
    "md-icon": {
      "&&": {
        minWidth: "40px",
        minHeight: "40px",
      },
    },
    "tablebox-icon": {
      "&&": {
        width: "28px",
        height: "28px",
        svg: {
          w: "20px",
          h: "20px",
        },
      },
    },
    "tablerow-icon": {
      svg: {
        width: "24px",
        height: "24px",
      },
    },
  },
  defaultProps: {
    variant: "solid",
    colorScheme: "primary",
    size: "md",
  },
};

const solidStylesForColorScheme: Record<string, SystemStyleObject> = {
  primary: {
    bg: "primary.700",
    borderColor: "primary.700",
    color: "white",
    _hover: {
      color: "white",
      bg: "primary.800",
      borderColor: "primary.800",
    },
    _active: {
      bg: "primary.700",
      shadow: "inset 0px 4px 7px #000000",
    },
    _disabled: {
      "&&": {
        opacity: 0.5,
        bg: "primary.600",
        borderColor: "primary.600",
        _active: {
          shadow: "none",
        },
      },
    },
    ".chakra-button__group[data-attached] > &:not(:last-of-type)": {
      marginEnd: "1px",
    },
  },
  secondary: {
    bg: "white",
    borderColor: "gray.300",
    color: "gray.700",
    _hover: {
      bg: "gray.100",
      borderColor: "gray.300",
      color: "gray.800",
    },
    _active: {
      bg: "gray.100",
      borderColor: "gray.300",
      color: "gray.800",
      shadow: `inset 0px 4px 6px ${transparentize(
        colors.gray["900"],
        1 - 0.1,
      )}`,
    },
    _disabled: {
      "&&": {
        bg: "white",
        borderColor: "gray.200",
        color: "gray.300",
        _hover: {
          bg: "white",
        },
        _active: {
          shadow: "none",
        },
      },
    },
  },
  tertiary: {
    bg: "teal.25",
    borderColor: "teal.100",
    color: "teal.900",
    _hover: {
      color: "teal.900",
      bg: "teal.50",
    },
    _active: {
      bg: "teal.50",
      shadow: `inset 0px 4px 6px ${transparentize(
        colors.primary["900"],
        1 - 0.2,
      )}`,
    },
    _disabled: {
      "&&": {
        opacity: 0.5,
        bg: "primary.50",
        borderColor: "primary.300",
        color: "primary.800",
        _active: {
          shadow: "none",
        },
      },
    },
  },
  destructive: {
    bg: "error.25",
    borderColor: "error.200",
    color: "error.700",
    _hover: {
      color: "error.700",
      bg: "error.100",
      borderColor: "error.300",
    },
    _active: {
      bg: "error.100",
      borderColor: "error.300",
      shadow: `inset 0px 4px 6px ${transparentize(
        colors.error["900"],
        1 - 0.15,
      )}`,
    },
    _disabled: {
      "&&": {
        opacity: 0.5,
        bg: "error.50",
        borderColor: "error.200",
        color: "error.700",
        _active: {
          shadow: "none",
        },
      },
    },
  },
};

const subtleStylesForColorScheme: Record<string, SystemStyleObject> = {
  primary: {
    color: "gray.500",
    _hover: {
      color: "gray.500",
      bg: "gray.25",
    },
    _active: {
      bg: "gray.50",
      color: "gray.700",
    },
    _disabled: {
      "&&": {
        opacity: 0.5,
        color: "gray.500",
        bg: "unset",
      },
    },
  },
  edit: {
    color: "teal.600",
    _hover: {
      bg: "teal.25",
      color: "teal.600",
    },
    _active: {
      bg: "teal.50",
      color: "teal.800",
    },
    _disabled: {
      "&&": {
        opacity: 0.5,
        color: "blue.600",
        bg: "unset",
      },
    },
  },
  destructive: {
    color: "error.600",
    _hover: {
      bg: "error.25",
      color: "error.600",
    },
    _active: {
      bg: "error.50",
      color: "error.800",
    },
    _disabled: {
      "&&": {
        opacity: 0.5,
        color: "error.600",
        bg: "unset",
      },
    },
  },
};

IconButton.defaultProps = {
  variant: "solid",
  colorScheme: "secondary",
  size: "xs-icon",
};

export default ButtonTheme;
